import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import WorshipIconPicker from './WorshipIconPicker';

type WorshipServiceRow = {
  id: number;
  serviceDate: string; // YYYY-MM-DD
  serviceName: string;
  label: string;
  title: string;
  subtitle: string;
  status: 'draft' | 'published';
  itemCount: number;
};

type WorshipServiceForm = {
  serviceDate: string;
  serviceName: string;
  label: string;
  title: string;
  subtitle: string;
  status: 'draft' | 'published';
};

type HeroForm = {
  label: string;
  title: string;
  subtitle: string;
};

type NewServiceForm = {
  serviceDate: string;
  serviceName: string;
  copyFromId: number | '';
};

type WorshipOrderRow = {
  id: number;
  icon: string;
  title: string;
  meta: string;
  tag: string;
  format: 'basic' | 'detail';
  description: string;
};

type WorshipOrderForm = {
  icon: string;
  title: string;
  meta: string;
  tag: string;
  format: 'basic' | 'detail';
  description: string;
};

type ServingTeamRow = {
  id: number;
  role: string;
  name: string;
};

type ServingTeamForm = {
  role: string;
  name: string;
};

const EMPTY_SERVICE_FORM: WorshipServiceForm = {
  serviceDate: '',
  serviceName: '',
  label: '',
  title: '',
  subtitle: '',
  status: 'draft',
};
const EMPTY_ORDER_FORM: WorshipOrderForm = { icon: '', title: '', meta: '', tag: '', format: 'basic', description: '' };
const EMPTY_SERVING_FORM: ServingTeamForm = { role: '', name: '' };

const WEEKDAYS = ['주일', '월', '화', '수', '목', '금', '토'];

function toYmd(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

/** 오늘이 주일이면 오늘, 아니면 다가오는 주일 */
function upcomingSunday(): string {
  const date = new Date();
  date.setDate(date.getDate() + ((7 - date.getDay()) % 7));
  return toYmd(date);
}

function formatServiceDate(value: string): string {
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return `${y}.${String(m).padStart(2, '0')}.${String(d).padStart(2, '0')} (${WEEKDAYS[date.getDay()]})`;
}

function toServiceForm(row: WorshipServiceRow): WorshipServiceForm {
  return {
    serviceDate: row.serviceDate,
    serviceName: row.serviceName,
    label: row.label ?? '',
    title: row.title ?? '',
    subtitle: row.subtitle ?? '',
    status: row.status === 'published' ? 'published' : 'draft',
  };
}

function errorMessage(err: unknown, fallback: string): string {
  return axios.isAxiosError(err) && err.response?.data?.message ? err.response.data.message : fallback;
}

export default function ManageWorship({ authChurch }: { authChurch: string }) {
  const [services, setServices] = useState<WorshipServiceRow[]>([]);
  const [currentDate, setCurrentDate] = useState<string | null>(null);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [serviceForm, setServiceForm] = useState<WorshipServiceForm>(EMPTY_SERVICE_FORM);
  const [serviceSaving, setServiceSaving] = useState(false);
  const [serviceMessage, setServiceMessage] = useState<string | null>(null);

  const [heroDraft, setHeroDraft] = useState<HeroForm | null>(null);
  const [heroSaving, setHeroSaving] = useState(false);
  const [heroError, setHeroError] = useState<string | null>(null);

  const [newForm, setNewForm] = useState<NewServiceForm | null>(null);
  const [creating, setCreating] = useState(false);
  const [newError, setNewError] = useState<string | null>(null);

  const [items, setItems] = useState<WorshipOrderRow[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [addForm, setAddForm] = useState<WorshipOrderForm>(EMPTY_ORDER_FORM);
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<WorshipOrderForm>(EMPTY_ORDER_FORM);

  const [servingTeam, setServingTeam] = useState<ServingTeamRow[]>([]);
  const [servingLoading, setServingLoading] = useState(true);
  const [servingError, setServingError] = useState<string | null>(null);

  const [servingAddForm, setServingAddForm] = useState<ServingTeamForm>(EMPTY_SERVING_FORM);
  const [servingAdding, setServingAdding] = useState(false);

  const [servingEditingId, setServingEditingId] = useState<number | null>(null);
  const [servingEditForm, setServingEditForm] = useState<ServingTeamForm>(EMPTY_SERVING_FORM);

  const selectedService = services.find((row) => row.id === selectedId) ?? null;

  // 날짜별로 묶은 예배 목록 (서버가 날짜 내림차순 → 같은 날은 예배명 순으로 내려준다)
  const servicesByDate = useMemo(() => {
    const groups: { date: string; rows: WorshipServiceRow[] }[] = [];
    services.forEach((row) => {
      const last = groups[groups.length - 1];
      if (last && last.date === row.serviceDate) last.rows.push(row);
      else groups.push({ date: row.serviceDate, rows: [row] });
    });
    return groups;
  }, [services]);

  /** 예배 목록을 다시 불러오고, preferId(없으면 기존 선택 → 이번주 첫 예배) 를 선택한다. */
  const fetchServices = (preferId?: number | null) => {
    setServicesLoading(true);
    setServicesError(null);
    axios
      .get(`${MainURL}/worship/services`, { params: { authChurch } })
      .then((res) => {
        const rows: WorshipServiceRow[] = res.data?.data ?? [];
        const current: string | null = res.data?.currentDate ?? null;
        setServices(rows);
        setCurrentDate(current);
        const keep = [preferId, selectedId].find((id) => id != null && rows.some((row) => row.id === id));
        const next = keep ?? rows.find((row) => row.serviceDate === current)?.id ?? rows[0]?.id ?? null;
        setSelectedId(next);
        const nextRow = rows.find((row) => row.id === next);
        setServiceForm(nextRow ? toServiceForm(nextRow) : EMPTY_SERVICE_FORM);
      })
      .catch((err) => {
        console.error('GET /worship/services', err);
        setServicesError('예배 목록을 불러오지 못했습니다.');
      })
      .finally(() => setServicesLoading(false));
  };

  const fetchOrder = (serviceId: number | null = selectedId) => {
    if (serviceId == null) {
      setItems([]);
      return;
    }
    setListLoading(true);
    setListError(null);
    axios
      .get(`${MainURL}/worship/order`, { params: { authChurch, serviceId } })
      .then((res) => setItems(res.data?.data ?? []))
      .catch((err) => {
        console.error('GET /worship/order', err);
        setListError('예배 순서를 불러오지 못했습니다.');
      })
      .finally(() => setListLoading(false));
  };

  const fetchServingTeam = () => {
    setServingLoading(true);
    setServingError(null);
    axios
      .get(`${MainURL}/worship/serving-team`, { params: { authChurch } })
      .then((res) => setServingTeam(res.data?.data ?? []))
      .catch((err) => {
        console.error('GET /worship/serving-team', err);
        setServingError('다음주 섬김이 목록을 불러오지 못했습니다.');
      })
      .finally(() => setServingLoading(false));
  };

  useEffect(() => {
    setSelectedId(null);
    fetchServices(null);
    fetchServingTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChurch]);

  useEffect(() => {
    cancelEdit();
    setServiceMessage(null);
    fetchOrder(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const selectService = (row: WorshipServiceRow) => {
    setSelectedId(row.id);
    setServiceForm(toServiceForm(row));
  };

  const openNewForm = () => {
    setNewError(null);
    setNewForm({ serviceDate: upcomingSunday(), serviceName: '', copyFromId: selectedId ?? '' });
  };

  const submitNewService = () => {
    if (!newForm || !newForm.serviceDate || !newForm.serviceName.trim()) return;
    setCreating(true);
    setNewError(null);
    const body = { authChurch, serviceDate: newForm.serviceDate, serviceName: newForm.serviceName.trim() };
    const request =
      newForm.copyFromId === ''
        ? axios.post(`${MainURL}/worship/services`, { ...body, status: 'draft' })
        : axios.post(`${MainURL}/worship/services/${newForm.copyFromId}/duplicate`, body);
    request
      .then((res) => {
        setNewForm(null);
        fetchServices(res.data?.id ?? null);
      })
      .catch((err) => {
        console.error('POST /worship/services', err);
        setNewError(errorMessage(err, '예배를 만들지 못했습니다.'));
      })
      .finally(() => setCreating(false));
  };

  const saveService = (status: 'draft' | 'published') => {
    if (selectedId == null) return;
    if (!serviceForm.serviceDate || !serviceForm.serviceName.trim()) {
      setServiceMessage('날짜와 예배명을 입력해 주세요.');
      return;
    }
    setServiceSaving(true);
    setServiceMessage(null);
    axios
      .put(`${MainURL}/worship/services/${selectedId}`, { authChurch, ...serviceForm, status })
      .then(() => {
        setServiceForm((prev) => ({ ...prev, status }));
        setServiceMessage(status === 'published' ? '발행되었습니다.' : '임시저장되었습니다.');
        fetchServices(selectedId);
      })
      .catch((err) => {
        console.error('PUT /worship/services/:id', err);
        setServiceMessage(errorMessage(err, '저장에 실패했습니다.'));
      })
      .finally(() => setServiceSaving(false));
  };

  const openHeroForm = () => {
    setHeroError(null);
    setHeroDraft({ label: serviceForm.label, title: serviceForm.title, subtitle: serviceForm.subtitle });
  };

  /** 히어로만 바로 저장한다. 날짜·예배명·발행 상태는 저장된 값을 그대로 보내 아래 편집 중인 값과 섞이지 않게 한다. */
  const saveHero = () => {
    if (!heroDraft || !selectedService) return;
    const serviceId = selectedService.id;
    setHeroSaving(true);
    setHeroError(null);
    axios
      .put(`${MainURL}/worship/services/${serviceId}`, { authChurch, ...toServiceForm(selectedService), ...heroDraft })
      .then(() => {
        setServices((prev) => prev.map((row) => (row.id === serviceId ? { ...row, ...heroDraft } : row)));
        setServiceForm((prev) => ({ ...prev, ...heroDraft }));
        setHeroDraft(null);
      })
      .catch((err) => {
        console.error('PUT /worship/services/:id', err);
        setHeroError(errorMessage(err, '저장에 실패했습니다.'));
      })
      .finally(() => setHeroSaving(false));
  };

  const removeService = () => {
    if (!selectedService) return;
    const name = `${formatServiceDate(selectedService.serviceDate)} ${selectedService.serviceName}`;
    if (!window.confirm(`${name}와(과) 이 예배의 순서 항목을 모두 삭제할까요?`)) return;
    axios
      .delete(`${MainURL}/worship/services/${selectedService.id}`, { params: { authChurch } })
      .then(() => {
        setSelectedId(null);
        fetchServices(null);
      })
      .catch((err) => console.error('DELETE /worship/services/:id', err));
  };

  const submitAdd = () => {
    if (!addForm.title.trim() || selectedId == null) return;
    setAdding(true);
    axios
      .post(`${MainURL}/worship/order`, { authChurch, serviceId: selectedId, ...addForm })
      .then(() => {
        setAddForm(EMPTY_ORDER_FORM);
        fetchOrder();
      })
      .catch((err) => console.error('POST /worship/order', err))
      .finally(() => setAdding(false));
  };

  const startEdit = (item: WorshipOrderRow) => {
    setEditingId(item.id);
    setEditForm({
      icon: item.icon,
      title: item.title,
      meta: item.meta,
      tag: item.tag,
      format: item.format,
      description: item.description,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_ORDER_FORM);
  };

  const submitEdit = (id: number) => {
    axios
      .put(`${MainURL}/worship/order/${id}`, { authChurch, ...editForm })
      .then(() => {
        cancelEdit();
        fetchOrder();
      })
      .catch((err) => console.error('PUT /worship/order/:id', err));
  };

  const removeItem = (id: number) => {
    if (!window.confirm('이 순서 항목을 삭제할까요?')) return;
    axios
      .delete(`${MainURL}/worship/order/${id}`, { params: { authChurch } })
      .then(() => fetchOrder())
      .catch((err) => console.error('DELETE /worship/order/:id', err));
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
    axios
      .put(`${MainURL}/worship/order/reorder`, { authChurch, orderedIds: next.map((row) => row.id) })
      .catch((err) => console.error('PUT /worship/order/reorder', err));
  };

  const submitServingAdd = () => {
    if (!servingAddForm.role.trim()) return;
    setServingAdding(true);
    axios
      .post(`${MainURL}/worship/serving-team`, { authChurch, ...servingAddForm })
      .then(() => {
        setServingAddForm(EMPTY_SERVING_FORM);
        fetchServingTeam();
      })
      .catch((err) => console.error('POST /worship/serving-team', err))
      .finally(() => setServingAdding(false));
  };

  const startServingEdit = (member: ServingTeamRow) => {
    setServingEditingId(member.id);
    setServingEditForm({ role: member.role, name: member.name });
  };

  const cancelServingEdit = () => {
    setServingEditingId(null);
    setServingEditForm(EMPTY_SERVING_FORM);
  };

  const submitServingEdit = (id: number) => {
    axios
      .put(`${MainURL}/worship/serving-team/${id}`, { authChurch, ...servingEditForm })
      .then(() => {
        cancelServingEdit();
        fetchServingTeam();
      })
      .catch((err) => console.error('PUT /worship/serving-team/:id', err));
  };

  const removeServingMember = (id: number) => {
    if (!window.confirm('이 섬김이 항목을 삭제할까요?')) return;
    axios
      .delete(`${MainURL}/worship/serving-team/${id}`, { params: { authChurch } })
      .then(() => fetchServingTeam())
      .catch((err) => console.error('DELETE /worship/serving-team/:id', err));
  };

  const moveServingMember = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= servingTeam.length) return;
    const next = [...servingTeam];
    [next[index], next[target]] = [next[target], next[index]];
    setServingTeam(next);
    axios
      .put(`${MainURL}/worship/serving-team/reorder`, { authChurch, orderedIds: next.map((row) => row.id) })
      .catch((err) => console.error('PUT /worship/serving-team/reorder', err));
  };

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">예배순서 관리</h2>
          <p className="manage-panel-desc">
            날짜와 예배(1부·2부·3부 등)마다 모바일 주보를 따로 편집합니다. 예배별로 발행해야 성도들에게 노출되고,
            지난 예배도 성도 앱에서 다시 볼 수 있습니다.
          </p>
        </div>
        {selectedService && (
          <div className="manage-block-actions">
            <span className={`manage-badge ${serviceForm.status === 'published' ? 'manage-badge--green' : 'manage-badge--amber'}`}>
              {serviceForm.status === 'published' ? '● 발행됨' : '● 작성중 · 미발행'}
            </span>
            <button type="button" className="manage-btn manage-btn--primary" onClick={openHeroForm}>
              상단 히어로 수정하기
            </button>
          </div>
        )}
      </div>

      <section className="manage-card-block">
        <div className="manage-block-head">
          <h3 className="manage-block-title">예배 선택</h3>
          <span className="manage-block-count">{services.length}</span>
          <button type="button" className="manage-btn manage-btn--sm manage-worship-service-add" onClick={openNewForm}>
            + 새 예배 추가
          </button>
        </div>
        <div className="manage-block-body">
          {newForm && (
            <div className="manage-worship-service-new">
              <label className="manage-field">
                날짜
                <input
                  type="date"
                  value={newForm.serviceDate}
                  onChange={(e) => setNewForm({ ...newForm, serviceDate: e.target.value })}
                />
              </label>
              <label className="manage-field">
                예배명
                <input
                  value={newForm.serviceName}
                  onChange={(e) => setNewForm({ ...newForm, serviceName: e.target.value })}
                  placeholder="예: 주일 1부예배"
                />
              </label>
              <label className="manage-field">
                순서 가져오기
                <select
                  value={newForm.copyFromId}
                  onChange={(e) => setNewForm({ ...newForm, copyFromId: e.target.value ? Number(e.target.value) : '' })}>
                  <option value="">빈 예배로 시작</option>
                  {services.map((row) => (
                    <option key={row.id} value={row.id}>
                      {formatServiceDate(row.serviceDate)} {row.serviceName} 복제
                    </option>
                  ))}
                </select>
              </label>
              <div className="manage-form-actions manage-worship-service-new-actions">
                <button
                  type="button"
                  className="manage-btn manage-btn--primary"
                  onClick={submitNewService}
                  disabled={creating || !newForm.serviceDate || !newForm.serviceName.trim()}>
                  {creating ? '만드는 중...' : '만들기'}
                </button>
                <button type="button" className="manage-btn" onClick={() => setNewForm(null)}>
                  취소
                </button>
              </div>
              {newError && <div className="manage-state manage-state--error manage-worship-service-new-error">{newError}</div>}
            </div>
          )}

          {servicesLoading && services.length === 0 && <div className="manage-state">불러오는 중입니다...</div>}
          {!servicesLoading && servicesError && <div className="manage-state manage-state--error">{servicesError}</div>}
          {!servicesLoading && !servicesError && services.length === 0 && !newForm && (
            <div className="manage-table-empty">등록된 예배가 없습니다. '새 예배 추가'로 이번 주 예배를 만들어 주세요.</div>
          )}

          <div className="manage-worship-service-groups">
            {servicesByDate.map((group) => (
              <div className="manage-worship-service-group" key={group.date}>
                <div className="manage-worship-service-date">
                  {formatServiceDate(group.date)}
                  {group.date === currentDate && <span className="manage-worship-service-current">이번주</span>}
                </div>
                <div className="manage-worship-service-chips">
                  {group.rows.map((row) => (
                    <button
                      type="button"
                      key={row.id}
                      className={`manage-worship-service-chip ${row.id === selectedId ? 'is-active' : ''}`}
                      onClick={() => selectService(row)}>
                      <span className={`manage-worship-service-dot ${row.status === 'published' ? 'is-published' : ''}`} aria-hidden />
                      {row.serviceName}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selectedService && (
      <>
      <div className="manage-grid-2col">
        <section className="manage-card-block">
          <div className="manage-block-head">
            <h3 className="manage-block-title">예배 순서 항목</h3>
            <span className="manage-block-count">{items.length}</span>
          </div>

          <div className="manage-worship-order-meta">
            <input
              type="date"
              className="manage-worship-order-meta-date"
              value={serviceForm.serviceDate}
              onChange={(e) => setServiceForm({ ...serviceForm, serviceDate: e.target.value })}
              aria-label="예배 날짜"
            />
            <input
              className="manage-worship-order-meta-name"
              value={serviceForm.serviceName}
              onChange={(e) => setServiceForm({ ...serviceForm, serviceName: e.target.value })}
              placeholder="예배명 (예: 주일 1부예배)"
            />
          </div>

          {listLoading && <div className="manage-state">불러오는 중입니다...</div>}
          {!listLoading && listError && <div className="manage-state manage-state--error">{listError}</div>}

          {!listLoading && !listError && (
            <>
              <div className="manage-worship-order-list">
                {items.length === 0 && (
                  <div className="manage-table-empty">등록된 순서 항목이 없습니다.</div>
                )}
                {items.map((item, index) =>
                  editingId === item.id ? (
                    <div className="manage-worship-order-card is-editing" key={item.id}>
                      <div className="manage-worship-order-row">
                        <WorshipIconPicker
                          value={editForm.icon}
                          onChange={(icon) => setEditForm({ ...editForm, icon })}
                        />
                        <div className="manage-worship-card-body">
                          <input
                            className="manage-worship-card-title-input"
                            value={editForm.title}
                            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                            placeholder="제목"
                          />
                          <input
                            className="manage-worship-card-meta-input"
                            value={editForm.meta}
                            onChange={(e) => setEditForm({ ...editForm, meta: e.target.value })}
                            placeholder="소제목"
                          />
                        </div>
                        <input
                          className="manage-worship-tag-input"
                          value={editForm.tag}
                          onChange={(e) => setEditForm({ ...editForm, tag: e.target.value })}
                          placeholder="담당자"
                        />
                      </div>
                      <div className="manage-worship-format-toggle">
                        <button
                          type="button"
                          className={`manage-worship-format-btn ${editForm.format === 'basic' ? 'is-active' : ''}`}
                          onClick={() => setEditForm({ ...editForm, format: 'basic' })}>
                          기본형
                        </button>
                        <button
                          type="button"
                          className={`manage-worship-format-btn ${editForm.format === 'detail' ? 'is-active' : ''}`}
                          onClick={() => setEditForm({ ...editForm, format: 'detail' })}>
                          설명형
                        </button>
                      </div>
                      {editForm.format === 'detail' && (
                        <textarea
                          className="manage-worship-description-input"
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          placeholder="추가 설명 (예: 찬송가 가사, 사도신경 전문 등)"
                          rows={3}
                        />
                      )}
                      <div className="manage-worship-card-actions">
                        <div className="manage-move-btns manage-move-btns--row">
                          <button type="button" onClick={() => moveItem(index, -1)} disabled={index === 0}>▲</button>
                          <button type="button" onClick={() => moveItem(index, 1)} disabled={index === items.length - 1}>▼</button>
                        </div>
                        <div className="manage-row-actions">
                          <button type="button" className="manage-btn manage-btn--sm manage-btn--primary" onClick={() => submitEdit(item.id)}>저장</button>
                          <button type="button" className="manage-btn manage-btn--sm" onClick={cancelEdit}>취소</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="manage-worship-row" key={item.id}>
                      <div className="manage-move-btns manage-move-btns--row manage-worship-row-move">
                        <button type="button" onClick={() => moveItem(index, -1)} disabled={index === 0}>▲</button>
                        <button type="button" onClick={() => moveItem(index, 1)} disabled={index === items.length - 1}>▼</button>
                      </div>
                      <span className="manage-worship-order-badge">{index + 1}</span>
                      <span className="manage-worship-row-icon" aria-hidden>{item.icon}</span>
                      <span className="manage-worship-row-title">{item.title}</span>
                      <span className="manage-worship-row-meta">{item.meta}</span>
                      {item.format === 'detail' && <span className="manage-worship-format-badge">설명형</span>}
                      <span className="manage-worship-tag">{item.tag}</span>
                      <div className="manage-worship-row-actions">
                        <button type="button" className="manage-worship-icon-btn" onClick={() => startEdit(item)} title="수정">✏️</button>
                        <button type="button" className="manage-worship-icon-btn" onClick={() => removeItem(item.id)} title="삭제">🗑</button>
                      </div>
                    </div>
                  ),
                )}
              </div>

              <div className="manage-worship-add-card">
                <div className="manage-worship-order-row">
                  <WorshipIconPicker
                    value={addForm.icon}
                    onChange={(icon) => setAddForm({ ...addForm, icon })}
                  />
                  <div className="manage-worship-card-body">
                    <input
                      className="manage-worship-card-title-input"
                      placeholder="제목 (예: 찬양과 경배)"
                      value={addForm.title}
                      onChange={(e) => setAddForm({ ...addForm, title: e.target.value })}
                    />
                    <input
                      className="manage-worship-card-meta-input"
                      placeholder="소제목 (예: 다함께 · 주 하나님 지으신 모든 세계)"
                      value={addForm.meta}
                      onChange={(e) => setAddForm({ ...addForm, meta: e.target.value })}
                    />
                  </div>
                  <input
                    className="manage-worship-tag-input"
                    placeholder="담당자"
                    value={addForm.tag}
                    onChange={(e) => setAddForm({ ...addForm, tag: e.target.value })}
                  />
                </div>
                <div className="manage-worship-format-toggle">
                  <button
                    type="button"
                    className={`manage-worship-format-btn ${addForm.format === 'basic' ? 'is-active' : ''}`}
                    onClick={() => setAddForm({ ...addForm, format: 'basic' })}>
                    기본형
                  </button>
                  <button
                    type="button"
                    className={`manage-worship-format-btn ${addForm.format === 'detail' ? 'is-active' : ''}`}
                    onClick={() => setAddForm({ ...addForm, format: 'detail' })}>
                    설명형
                  </button>
                </div>
                {addForm.format === 'detail' && (
                  <textarea
                    className="manage-worship-description-input"
                    value={addForm.description}
                    onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                    placeholder="추가 설명 (예: 찬송가 가사, 사도신경 전문 등)"
                    rows={3}
                  />
                )}
                <div className="manage-form-actions">
                  <button type="button" className="manage-btn manage-btn--primary" onClick={submitAdd} disabled={adding || !addForm.title.trim()}>
                    추가
                  </button>
                </div>
              </div>
            </>
          )}
        </section>

        <aside className="manage-side-stack">
          <div className="manage-side-card manage-worship-phone">
            <div className="manage-worship-phone-notch">
              {serviceForm.label || '모바일 주보'}
              <small>실시간 미리보기</small>
            </div>
            <div className="manage-worship-phone-screen">
              <div className="manage-worship-phone-title">{serviceForm.title || '제목 미입력'}</div>
              {(!!serviceForm.serviceDate || !!serviceForm.serviceName) && (
                <div className="manage-worship-phone-meta">
                  {[serviceForm.serviceDate && formatServiceDate(serviceForm.serviceDate), serviceForm.serviceName]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
              {items.length === 0 && <div className="manage-side-empty">등록된 순서 항목이 없습니다.</div>}
              {items.map((item) => (
                <div className="manage-worship-prow" key={item.id}>
                  <b>{item.title}</b>
                  <span>{item.meta}</span>
                </div>
              ))}
              {servingTeam.length > 0 && (
                <div className="manage-worship-phone-title" style={{ marginTop: 10 }}>
                  다음주 섬김이
                </div>
              )}
              {servingTeam.map((member) => (
                <div className="manage-worship-prow" key={member.id}>
                  <b>{member.role}</b>
                  <span>{member.name}</span>
                </div>
              ))}
              <div className="manage-worship-sync-note">실시간 동기화 미리보기</div>
            </div>
          </div>

          <div className="manage-side-card">
            <div className="manage-side-card-head">현황</div>
            <div className="manage-side-card-body">
              <div className="manage-stat-row">
                <span className="manage-stat-row-label">등록된 항목</span>
                <span className="manage-stat-row-count">{items.length}개</span>
              </div>
              <div className="manage-stat-row">
                <span className="manage-stat-row-label">발행 상태</span>
                <span className="manage-stat-row-count">{serviceForm.status === 'published' ? '발행됨' : '미발행'}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <div className="manage-worship-footer">
        {serviceMessage && <span className="manage-inline-message">{serviceMessage}</span>}
        <div className="manage-form-actions manage-worship-footer-actions">
          <button type="button" className="manage-btn manage-btn--danger" onClick={removeService} disabled={serviceSaving}>
            예배 삭제
          </button>
          <button type="button" className="manage-btn" onClick={() => saveService('draft')} disabled={serviceSaving}>
            {serviceForm.status === 'published' ? '발행 취소(임시저장)' : '임시저장'}
          </button>
          <button type="button" className="manage-btn manage-btn--primary" onClick={() => saveService('published')} disabled={serviceSaving}>
            {serviceSaving ? '처리 중...' : '🚀 발행하기'}
          </button>
        </div>
      </div>
      </>
      )}

      <section className="manage-card-block">
        <div className="manage-block-head">
          <h3 className="manage-block-title">다음주 섬김이</h3>
          <span className="manage-block-count">{servingTeam.length}</span>
        </div>

        {servingLoading && <div className="manage-state">불러오는 중입니다...</div>}
        {!servingLoading && servingError && <div className="manage-state manage-state--error">{servingError}</div>}

        {!servingLoading && !servingError && (
          <>
            <div className="manage-worship-order-list">
              {servingTeam.length === 0 && (
                <div className="manage-table-empty">등록된 섬김이 항목이 없습니다.</div>
              )}
              {servingTeam.map((member, index) =>
                servingEditingId === member.id ? (
                  <div className="manage-worship-order-card is-editing" key={member.id}>
                    <div className="manage-worship-order-row">
                      <div className="manage-worship-card-body">
                        <input
                          className="manage-worship-card-title-input"
                          value={servingEditForm.role}
                          onChange={(e) => setServingEditForm({ ...servingEditForm, role: e.target.value })}
                          placeholder="역할 (예: 대표기도)"
                        />
                        <input
                          className="manage-worship-card-meta-input"
                          value={servingEditForm.name}
                          onChange={(e) => setServingEditForm({ ...servingEditForm, name: e.target.value })}
                          placeholder="이름"
                        />
                      </div>
                    </div>
                    <div className="manage-worship-card-actions">
                      <div className="manage-move-btns manage-move-btns--row">
                        <button type="button" onClick={() => moveServingMember(index, -1)} disabled={index === 0}>▲</button>
                        <button type="button" onClick={() => moveServingMember(index, 1)} disabled={index === servingTeam.length - 1}>▼</button>
                      </div>
                      <div className="manage-row-actions">
                        <button type="button" className="manage-btn manage-btn--sm manage-btn--primary" onClick={() => submitServingEdit(member.id)}>저장</button>
                        <button type="button" className="manage-btn manage-btn--sm" onClick={cancelServingEdit}>취소</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="manage-worship-row" key={member.id}>
                    <div className="manage-move-btns manage-move-btns--row manage-worship-row-move">
                      <button type="button" onClick={() => moveServingMember(index, -1)} disabled={index === 0}>▲</button>
                      <button type="button" onClick={() => moveServingMember(index, 1)} disabled={index === servingTeam.length - 1}>▼</button>
                    </div>
                    <span className="manage-worship-row-title">{member.role}</span>
                    <span className="manage-worship-row-meta">{member.name}</span>
                    <div className="manage-worship-row-actions">
                      <button type="button" className="manage-worship-icon-btn" onClick={() => startServingEdit(member)} title="수정">✏️</button>
                      <button type="button" className="manage-worship-icon-btn" onClick={() => removeServingMember(member.id)} title="삭제">🗑</button>
                    </div>
                  </div>
                ),
              )}
            </div>

            <div className="manage-worship-add-card">
              <div className="manage-worship-order-row">
                <div className="manage-worship-card-body">
                  <input
                    className="manage-worship-card-title-input"
                    placeholder="역할 (예: 대표기도, 헌금위원)"
                    value={servingAddForm.role}
                    onChange={(e) => setServingAddForm({ ...servingAddForm, role: e.target.value })}
                  />
                  <input
                    className="manage-worship-card-meta-input"
                    placeholder="이름"
                    value={servingAddForm.name}
                    onChange={(e) => setServingAddForm({ ...servingAddForm, name: e.target.value })}
                  />
                </div>
              </div>
              <div className="manage-form-actions">
                <button
                  type="button"
                  className="manage-btn manage-btn--primary"
                  onClick={submitServingAdd}
                  disabled={servingAdding || !servingAddForm.role.trim()}>
                  추가
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      {heroDraft && (
        <div className="manage-modal-overlay" onClick={() => setHeroDraft(null)}>
          <div className="manage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-modal-header">
              <h3>상단 히어로 수정</h3>
              <button type="button" className="manage-modal-close" onClick={() => setHeroDraft(null)}>×</button>
            </div>
            <div className="manage-modal-body">
              <p className="manage-modal-desc">
                상단 히어로는 성도 앱 예배순서 화면 맨 위에 보이는 머리글입니다. 제목은 크게, 라벨은 작은 꼬리표로,
                부제는 제목 아래 안내 문구로 표시되고, 비워 두면 기본 환영 문구가 나옵니다.
                <br />
                지금 선택한 예배
                {selectedService && ` (${formatServiceDate(selectedService.serviceDate)} ${selectedService.serviceName})`}
                에만 적용되며, 저장해도 발행 상태는 바뀌지 않습니다.
              </p>
              <div className="manage-form">
                <label className="manage-field">
                  <span>제목</span>
                  <input
                    value={heroDraft.title}
                    onChange={(e) => setHeroDraft({ ...heroDraft, title: e.target.value })}
                    placeholder="예: 2026년 6월 7일 주일예배"
                  />
                </label>
                <label className="manage-field">
                  <span>라벨</span>
                  <input
                    value={heroDraft.label}
                    onChange={(e) => setHeroDraft({ ...heroDraft, label: e.target.value })}
                    placeholder="예: 주일 2부 예배 · 오전 11:00"
                  />
                </label>
                <label className="manage-field">
                  <span>부제</span>
                  <textarea
                    rows={2}
                    value={heroDraft.subtitle}
                    onChange={(e) => setHeroDraft({ ...heroDraft, subtitle: e.target.value })}
                    placeholder="예: 오전 11:00 · 대예배실 · 은혜와 진리로 드리는 공동체 예배"
                  />
                </label>
                {heroError && <span className="manage-inline-message">{heroError}</span>}
              </div>
            </div>
            <div className="manage-modal-footer">
              <button type="button" className="manage-btn" onClick={() => setHeroDraft(null)}>취소</button>
              <button type="button" className="manage-btn manage-btn--primary" onClick={saveHero} disabled={heroSaving}>
                {heroSaving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
